import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Plus, X, Edit, Palette, Tag as TagIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const TAG_COLORS = [
  "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6",
  "#EC4899", "#06B6D4", "#84CC16", "#F97316", "#6366F1"
];

export default function TagManager({ isOpen, onClose, onTagsUpdated }) {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTags(response.data);
    } catch (error) {
      toast.error("Failed to load tags");
      console.error("Error fetching tags:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error("Tag name is required");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('tag_name', newTagName.trim());
      params.append('tag_color', newTagColor);

      await axios.post(`${API}/api/tags?${params}`, null, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success("Tag created successfully!");
      setNewTagName("");
      setNewTagColor(TAG_COLORS[0]);
      fetchTags();
      if (onTagsUpdated) onTagsUpdated();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Tag already exists");
      } else {
        toast.error("Failed to create tag");
      }
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm("Are you sure? This will remove the tag from all favorites and files.")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Tag deleted successfully!");
      fetchTags();
      if (onTagsUpdated) onTagsUpdated();
    } catch (error) {
      toast.error("Failed to delete tag");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl text-purple-900 flex items-center gap-3">
            <TagIcon className="w-6 h-6 text-purple-600" />
            Manage Tags
          </DialogTitle>
          <DialogDescription>
            Create and manage custom tags for organizing your favorites and files
          </DialogDescription>
        </DialogHeader>

        {/* Create New Tag */}
        <div className="space-y-4 border-b pb-6">
          <h3 className="font-semibold text-gray-900">Create New Tag</h3>
          <div className="flex gap-3">
            <Input
              placeholder="Enter tag name..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              className="flex-1"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
            />
            
            {/* Color Picker */}
            <div className="flex gap-1">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setNewTagColor(color)}
                  className={`w-8 h-8 rounded-full border-2 ${
                    newTagColor === color ? 'border-gray-800' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            
            <Button
              onClick={handleCreateTag}
              disabled={!newTagName.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create
            </Button>
          </div>
        </div>

        {/* Existing Tags */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Your Tags ({tags.length})</h3>
          
          {loading ? (
            <div className="text-center py-4">Loading tags...</div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TagIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              No tags created yet. Create your first tag above!
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: tag.tag_color }}
                    />
                    <span className="font-medium text-gray-900">{tag.tag_name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {tag.usage_count} items
                    </Badge>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTag(tag.id)}
                    className="text-red-600 hover:bg-red-100"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} variant="outline">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}